import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { UserPlus, Loader2, Trash2, Shield } from "lucide-react";

export default function AdminAdmins() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [data, setData] = useState({ admins: [], pending_invites: [] });
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState("");

  const reload = async () => {
    setLoading(true);
    const r = await api.get("/admin/admins");
    setData(r.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setInviting(true);
    setError("");
    try {
      await api.post("/admin/admins/invite", { email: email.trim().toLowerCase() });
      setEmail("");
      await reload();
    } catch (err) {
      setError(err?.response?.data?.detail || "Invitation failed");
    } finally { setInviting(false); }
  };

  const remove = async (em) => {
    if (!window.confirm(`Revoke admin access for ${em}?`)) return;
    await api.delete(`/admin/admins/${encodeURIComponent(em)}`);
    reload();
  };

  return (
    <div data-testid="admin-admins-page">
      <header className="mb-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne mb-2">Access</p>
        <h1 className="font-serif text-3xl text-aubergine">Admins</h1>
        <p className="mt-2 text-sm text-aubergine/65">Members on the allowlist can sign in to the admin console with Google.</p>
      </header>

      {isSuper && (
        <div className="mb-6 rounded-lg border border-aubergine/10 bg-white p-5">
          <form onSubmit={invite} className="flex flex-col sm:flex-row gap-3" data-testid="invite-admin-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="new-admin@company.com"
              required
              data-testid="invite-admin-email"
              className="flex-1 rounded-md border border-aubergine/15 bg-white px-3 py-2.5 text-sm outline-none focus:border-champagne"
            />
            <button type="submit" disabled={inviting} data-testid="invite-admin-submit" className="inline-flex items-center justify-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2.5 font-mono text-xs disabled:opacity-60">
              {inviting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
              Invite admin
            </button>
          </form>
          {error && <p className="mt-3 text-sm text-signal-red" data-testid="invite-admin-error">{error}</p>}
          <p className="mt-3 text-xs text-aubergine/55">The invited email will be able to sign in with Google. They&apos;ll appear in the list below as soon as they first sign in.</p>
        </div>
      )}
      {!isSuper && (
        <div className="mb-6 rounded-lg border border-aubergine/10 bg-bone/40 p-4 text-xs text-aubergine/65" data-testid="admins-readonly-note">
          Only super-admins can invite or remove admins.
        </div>
      )}

      <div className="rounded-lg border border-aubergine/10 bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="admins-table">
          <thead className="bg-bone/60">
            <tr className="text-left text-[11px] font-mono uppercase tracking-[0.12em] text-aubergine/55">
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3 hidden md:table-cell">Name</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3 hidden lg:table-cell">Last login</th>
              {isSuper && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isSuper ? 5 : 4} className="px-4 py-8 text-center text-aubergine/55">Loading…</td></tr>
            ) : data.admins.map((a) => (
              <tr key={a.email} className="border-t border-aubergine/10">
                <td className="px-4 py-3 font-medium">{a.email}</td>
                <td className="px-4 py-3 hidden md:table-cell text-aubergine/65">{a.name || <span className="italic text-aubergine/35">—</span>}</td>
                <td className="px-4 py-3">
                  {a.role === "super_admin" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-champagne/15 text-aubergine px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider"><Shield className="h-3 w-3" /> Super-admin</span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-aubergine/10 text-aubergine/65 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider">Admin</span>
                  )}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell text-aubergine/55">{a.last_login_at ? new Date(a.last_login_at).toLocaleString() : <span className="italic text-aubergine/35">never</span>}</td>
                {isSuper && (
                  <td className="px-4 py-3 text-right">
                    {a.role !== "super_admin" && (
                      <button onClick={() => remove(a.email)} data-testid={`admin-remove-${a.email}`} className="text-aubergine/55 hover:text-signal-red" title="Revoke access">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.pending_invites && data.pending_invites.length > 0 && (
        <div className="mt-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-2">Pending invitations</p>
          <ul className="rounded-lg border border-aubergine/10 bg-white divide-y divide-aubergine/10">
            {data.pending_invites.map((p) => (
              <li key={p.invite_id} className="px-4 py-3 flex items-center justify-between text-sm">
                <span className="text-aubergine">{p.email}</span>
                <span className="text-xs text-aubergine/55">Invited by {p.invited_by}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
